/**
 * 추출 정보 칸 (D-3) — 상세 하단 접이식.
 *
 * 룰 provenance의 추출 출처(model_id·extracted_at·criteria_version·source_url·source_fingerprint·
 * tool_version)와 입력/출력 토큰(provenance.usage — Phase 3 산출, 없으면 "미기록")을 보인다.
 * 전개본이면 baked_by·unit_tier도. 순수 프리젠테이션(ARCHITECTURE §2).
 *
 * 값 없음 3종(D-5): criteria_version 없으면 "옛 기준", 그 밖의 옛 추출 값은 "미기록".
 */
import React from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Button from "components/atoms/Button";
import { NOT_RECORDED_TEXT, formatDateTime } from "utils/doseRule";
import { DosePromptRef, DoseProvenancePrompts } from "services/api/doseRules/types";
import { openPromptTextsSlide, PromptTextsStep } from "./openPromptTextsSlide";

export interface DoseRuleExtractionInfoProps {
  provenance: Record<string, unknown>;
  /** 전개본이면 baked_by·unit_tier도 보인다. */
  isDerived: boolean;
  /** 슬라이드 제목 `룰 #<ruleId>`에 쓴다. */
  ruleId: string;
  /** 전개본의 대표 룰 id(상세 응답 최상위 `detail.derivedFrom`) — 슬라이드 제목 전개본 변형용. */
  derivedFrom?: string | null;
}

const Fold = styled.details`
  margin-top: 12px;
  border-top: 1px solid ${COLOR_SYSTEM.get("Gray")?.[50]};
  padding-top: 8px;

  summary {
    cursor: pointer;
    font-size: 12px;
    font-weight: 700;
    color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  }
`;

/** Step 2 보관 시각 — 그리드 위 전폭 줄. 보조 문구가 옆에 붙어야 해서 2열 칸에 넣지 않는다. */
const Step2Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  margin-top: 8px;
  font-size: 12px;
`;

/** 보조 문구 — 보관본 시각이 제품 카드 시각과 다를 수 있음을 흐리게 알린다(09-20 D-5 보존 경로). */
const Step2Hint = styled.span`
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  font-weight: 400;
`;

/** 추출 프롬프트 STEP2/STEP3 — 그리드 아래 전폭 줄(Step2Row 관례). Val 끝에 [보낸 전문 보기]. */
const PromptRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  margin-top: 8px;
  font-size: 12px;
`;

/** STEP2 프롬프트 줄 아래 보조 문구(prompts.step2가 있을 때). */
const PromptHint = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px 16px;
  margin-top: 8px;
  font-size: 12px;
`;

const Field = styled.div`
  display: flex;
  gap: 6px;
  align-items: baseline;
`;

const Key = styled.span`
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  min-width: 80px;
  flex-shrink: 0;
`;

const Val = styled.span`
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  font-weight: 600;
  word-break: break-all;
`;

const str = (v: unknown): string | null =>
  typeof v === "string" && v.trim() ? v : typeof v === "number" ? String(v) : null;

const KV: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Field>
    <Key>{label}</Key>
    <Val>{children}</Val>
  </Field>
);

const DoseRuleExtractionInfo: React.FC<DoseRuleExtractionInfoProps> = ({
  provenance,
  isDerived,
  ruleId,
  derivedFrom,
}) => {
  const p = provenance ?? {};
  const modelId = str(p.model_id);
  const extractedAt = str(p.extracted_at);
  // 재추출 단계 옵션(spec 2026-09-29): 룰만이면 `from_step=step3`, 보관본 시각은 `step2_extracted_at`.
  const fromStep = str(p.from_step);
  const step2ExtractedAt = str(p.step2_extracted_at);
  const isRuleOnly = fromStep === "step3";
  const criteriaVersion = str(p.criteria_version);
  const sourceUrl = str(p.source_url);
  const sourceFingerprint = str(p.source_fingerprint);
  const toolVersion = str(p.tool_version);
  const usage = p.usage && typeof p.usage === "object" ? (p.usage as Record<string, unknown>) : null;
  const bakedBy = str(p.baked_by);
  const unitTier = str(p.unit_tier);

  const usageText = (() => {
    if (!usage) return NOT_RECORDED_TEXT;
    const s2i = str(usage.step2_input_tokens);
    const s2o = str(usage.step2_output_tokens);
    const s3i = str(usage.step3_input_tokens);
    const s3o = str(usage.step3_output_tokens);
    if (!s2i && !s2o && !s3i && !s3o) return NOT_RECORDED_TEXT;
    return `STEP2 ${s2i ?? "?"}/${s2o ?? "?"} · STEP3 ${s3i ?? "?"}/${s3o ?? "?"} (입력/출력)`;
  })();

  // 추출 프롬프트 참조(D-4). prompts 키가 없거나 해당 단계가 null이면 그 줄은 "미기록"·버튼 없음.
  // 단계별로 독립 판정한다(STEP2만 null인 경우 포함).
  const prompts = (p.prompts as DoseProvenancePrompts | undefined) ?? undefined;
  const step2Ref = prompts?.step2 ?? null;
  const step3Ref = prompts?.step3 ?? null;

  const renderPromptRow = (step: PromptTextsStep, ref: DosePromptRef | null) => (
    <PromptRow data-testid={`dose-extract-prompt-${step === "STEP2" ? "step2" : "step3"}`}>
      <Key>{step === "STEP2" ? "추출 프롬프트 STEP2" : "추출 프롬프트 STEP3"}</Key>
      {ref ? (
        <>
          <Val>
            {`#${ref.prompt_id} · 라벨 "${ref.prompt_version || "라벨 없음"}" · 지문 ${ref.template_sha256.slice(0, 12)} · ${ref.model}`}
          </Val>
          <Button
            size="XSmall"
            type="Text"
            variant="Tinted"
            color="Primary"
            onClick={() =>
              openPromptTextsSlide({
                step,
                ruleId,
                promptRef: ref,
                isDerived,
                derivedFrom: derivedFrom ?? null,
              })
            }
          >
            보낸 전문 보기
          </Button>
        </>
      ) : (
        <Val>{NOT_RECORDED_TEXT}</Val>
      )}
    </PromptRow>
  );

  return (
    <Fold data-testid="dose-extraction-info">
      <summary>추출 정보</summary>
      {/* Step 2 보관 시각 — 룰 추출 시각 위. 룰만 재추출의 재료로 보관된 STEP2 산출의 시각이다. */}
      <Step2Row data-testid="dose-step2-snapshot-time">
        <Key>Step 2 보관 시각</Key>
        <Val>
          {step2ExtractedAt ? formatDateTime(step2ExtractedAt) : NOT_RECORDED_TEXT}
        </Val>
        <Step2Hint>
          재추출 재료로 보관된 Step 2 산출의 시각 — 제품 카드 값의 추출 시각과 다를 수 있음
        </Step2Hint>
      </Step2Row>
      <Grid>
        <KV label="룰 추출 모델 (STEP3)">{modelId ?? NOT_RECORDED_TEXT}</KV>
        <KV label="룰 추출 시각">
          {extractedAt ? formatDateTime(extractedAt) : NOT_RECORDED_TEXT}
          {isRuleOnly ? " (룰만)" : ""}
        </KV>
        <KV label="기준 버전">{criteriaVersion ?? "옛 기준"}</KV>
        <KV label="원문">
          {sourceUrl ? (
            <a href={sourceUrl} target="_blank" rel="noreferrer">
              원문(nedrug) ↗
            </a>
          ) : (
            NOT_RECORDED_TEXT
          )}
        </KV>
        <KV label="입력/출력 토큰">{usageText}</KV>
        <KV label="추출 도구">{toolVersion ?? NOT_RECORDED_TEXT}</KV>
        <KV label="원문 지문">{sourceFingerprint ?? NOT_RECORDED_TEXT}</KV>
        {isDerived ? (
          <>
            <KV label="전개 출처">{bakedBy ?? NOT_RECORDED_TEXT}</KV>
            <KV label="단위 tier">{unitTier ?? NOT_RECORDED_TEXT}</KV>
          </>
        ) : null}
      </Grid>
      {/* 추출 프롬프트 두 줄 — 그리드 아래 전폭(화면설계 5). "이 룰은 어떤 지시문으로 뽑혔나". */}
      {renderPromptRow("STEP2", step2Ref)}
      {step2Ref ? (
        <PromptHint>제품 카드 값은 다른 Step 2 실행에서 나왔을 수 있음</PromptHint>
      ) : null}
      {renderPromptRow("STEP3", step3Ref)}
    </Fold>
  );
};

export default DoseRuleExtractionInfo;
